/**
 * Campaign List V2 — table columns + ComplexFilter configs.
 *
 * Wrapper query (adset:insights):
 * - Analysis by Campaign Name: campaign_name, campaign_id
 * - Analysis by Campaign + Ad Group: + ad_group_name, ad_group_id
 * - measures: spends/... plus status, campaign_status, targeting, portfolio_name, campaign_budget,
 *             campaign_bid_strategy, campaign_format, adgroup_status, campaign_burn_rate,
 *             campaign_type
 */

import { commonFilterOperators } from "../../components/Filter/FilterData/quadrantFilter";

export const CAMPAIGN_LIST_V2_DEFAULT_DIMENSIONS = [
  "campaign_name",
  "campaign_id",
];

export const CAMPAIGN_LIST_V2_CAMPAIGN_ADGROUP_DIMENSIONS = [
  "campaign_name",
  "campaign_id",
  "ad_group_name",
  "ad_group_id",
];

export const CAMPAIGN_LIST_V2_GROUP_BY_OPTIONS = [
  {
    label: "Campaign Name",
    value: "campaign",
    dimensions: CAMPAIGN_LIST_V2_DEFAULT_DIMENSIONS,
  },
  {
    label: "Campaign + Ad Group",
    value: "campaign_adgroup",
    dimensions: CAMPAIGN_LIST_V2_CAMPAIGN_ADGROUP_DIMENSIONS,
  },
];

export const CAMPAIGN_LIST_V2_PAGE_MEASURES = [
  "spends",
  "impressions",
  "clicks",
  "orders",
  "revenue",
  "roas",
  "ctr",
  "cpc",
  "cpm",
  "cvr",
  "aov",
  "acos",
];

export const CAMPAIGN_LIST_V2_ALWAYS_MEASURES = [
  "status",
  "campaign_status",
  "targeting",
  "portfolio_name",
  "campaign_budget",
  "campaign_bid_strategy",
  "campaign_format",
  "adgroup_status",
  "campaign_burn_rate",
  "campaign_type",
];

const MAX_WIDTH = 220;

export const CAMPAIGN_LIST_V2_COLUMNS = [
  // Sticky left cols
  {
    key: "status",
    title: "Status",
    shortTitle: "Status",
    type: "string",
    minWidth: 100,
    isSticky: true,
    alignment: "left",
    isAttribute: true,
  },
  {
    key: "campaign_name",
    innerKeys: {
      productName: "campaign_name",
      asin: { label: "Campaign ID", value: "campaign_id" },
    },
    title: "Campaign Name",
    shortTitle: "Campaign Name",
    type: "string",
    minWidth: 240,
    isSticky: true,
    isDimension: true,
    alignment: "left",
  },
  {
    key: "ad_group_id",
    innerKeys: {
      productName: "ad_group_name",
      asin: { label: "Ad Group ID", value: "ad_group_id" },
    },
    showAdgroupStatusBadge: true,
    title: "Ad Group",
    shortTitle: "Ad Group",
    type: "string",
    minWidth: 220,
    isSticky: false,
    isDimension: true,
    alignment: "left",
  },
  {
    key: "campaign_budget",
    title: "Campaign Budget",
    shortTitle: "Campaign Budget",
    type: "number",
    dataIndex: "campaign_budget",
    minWidth: 130,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    isAttribute: true,
  },
  {
    key: "campaign_bid_strategy",
    title: "Bid Strategy",
    shortTitle: "Bid Strategy",
    type: "string",
    minWidth: 170,
    alignment: "left",
    isAttribute: true,
  },

  // Non-sticky dimensions/attributes
  {
    key: "targeting",
    title: "Targeting Type",
    shortTitle: "Targeting",
    type: "string",
    minWidth: 160,
    alignment: "left",
    isAttribute: true,
  },
  {
    key: "portfolio_name",
    title: "Portfolio",
    shortTitle: "Portfolio",
    type: "string",
    minWidth: 160,
    alignment: "left",
    isAttribute: true,
  },

  // Metrics
  {
    key: "spends",
    title: "Spends",
    shortTitle: "Spends",
    type: "number",
    dataIndex: "spends",
    minWidth: 110,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "impressions",
    title: "Impressions",
    shortTitle: "Impressions",
    type: "number",
    dataIndex: "impressions",
    minWidth: 120,
    alignment: "right",
    maximumFractionDigits: 0,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "clicks",
    title: "Clicks",
    shortTitle: "Clicks",
    type: "number",
    dataIndex: "clicks",
    minWidth: 100,
    alignment: "right",
    maximumFractionDigits: 0,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "orders",
    title: "Orders",
    shortTitle: "Orders",
    type: "number",
    dataIndex: "orders",
    minWidth: 100,
    alignment: "right",
    maximumFractionDigits: 0,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "revenue",
    title: "Revenue",
    shortTitle: "Revenue",
    type: "number",
    dataIndex: "revenue",
    minWidth: 110,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "roas",
    title: "ROAS",
    shortTitle: "ROAS",
    type: "number",
    dataIndex: "roas",
    minWidth: 90,
    alignment: "right",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "ctr",
    title: "CTR",
    shortTitle: "CTR",
    type: "number",
    dataIndex: "ctr",
    minWidth: 90,
    alignment: "right",
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "cpc",
    title: "CPC",
    shortTitle: "CPC",
    type: "number",
    dataIndex: "cpc",
    minWidth: 90,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  {
    key: "cpm",
    title: "CPM",
    shortTitle: "CPM",
    type: "number",
    dataIndex: "cpm",
    minWidth: 90,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  {
    key: "cvr",
    title: "CVR",
    shortTitle: "CVR",
    type: "number",
    dataIndex: "cvr",
    minWidth: 90,
    alignment: "right",
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["FUNNEL"],
  },
  {
    key: "aov",
    title: "AOV",
    shortTitle: "AOV",
    type: "number",
    dataIndex: "aov",
    minWidth: 90,
    alignment: "right",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  {
    key: "acos",
    title: "ACOS",
    shortTitle: "ACOS",
    type: "number",
    dataIndex: "acos",
    minWidth: 90,
    alignment: "right",
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  {
    key: "campaign_burn_rate",
    title: "Burn Rate",
    shortTitle: "Burn Rate",
    type: "number",
    dataIndex: "campaign_burn_rate",
    minWidth: 100,
    alignment: "right",
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
];

export function getCampaignListV2Columns(dimensions = CAMPAIGN_LIST_V2_DEFAULT_DIMENSIONS) {
  const dimensionSet = new Set(dimensions || []);
  let cumulativeStickyLeft = 0;

  return CAMPAIGN_LIST_V2_COLUMNS.filter((column) => {
    if (column?.isDimension && !dimensionSet.has(column.key)) return false;
    return true;
  }).map((column) => {
    const minWidth = column.minWidth || 120;
    const updated = {
      ...column,
      stickyLeft: cumulativeStickyLeft,
    };
    if (column.isSticky) {
      cumulativeStickyLeft += minWidth;
    }
    return updated;
  });
}

// ---------------- ComplexFilterWidget configs ----------------
export const FiltersDimensionDataCampaignListV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Campaign Name",
        id: "campaign_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "campaign_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "campaign_names_api_key",
        backendSearch: true,
        grouping: "Campaign Name",
      },
      {
        name: "Portfolio Name",
        id: "portfolio_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "portfolio_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "portfolio_names_api_key",
        backendSearch: true,
        grouping: "Portfolio Name",
      },
      {
        name: "Campaign Type",
        id: "campaign_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "SP,SD,SB(V),SB",
        grouping: "Campaign Type",
      },
      {
        name: "Targeting Type",
        id: "targeting",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey:
          "G.Keyword,C.Keyword,NB.Keyword,Audience,Automatic,NB.Product,B.Keyword,B.Audience,B.Product",
        grouping: "Targeting Type",
      },
      {
        name: "Campaign Status",
        id: "campaign_status",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "PAUSED,ENABLED,ARCHIVED",
        grouping: "Campaign Status",
      },
      {
        name: "Campaign Format",
        id: "campaign_format",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "Prospect,Retarget",
        grouping: "Campaign Format",
      },
      {
        name: "Bid Strategy",
        id: "campaign_bid_strategy",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "Down,Up & Down",
        grouping: "Bid Strategy",
      },
      {
        name: "Ad Group Name",
        id: "ad_group_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "ad_group_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "ad_group_names_api_key",
        backendSearch: true,
        grouping: "Ad Group Name",
      },
    ],
  },
};

export const FiltersMeasuresDataCampaignListV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Spends",
        id: "spends",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Spends",
      },
      {
        name: "Impressions",
        id: "impressions",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Impressions",
      },
      {
        name: "Clicks",
        id: "clicks",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Clicks",
      },
      {
        name: "Orders",
        id: "orders",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Orders",
      },
      {
        name: "Revenue",
        id: "revenue",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Revenue",
      },
      {
        name: "ROAS",
        id: "roas",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ROAS",
      },
      {
        name: "CTR",
        id: "ctr",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CTR",
      },
      {
        name: "CPC",
        id: "cpc",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CPC",
      },
      {
        name: "CPM",
        id: "cpm",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CPM",
      },
      {
        name: "CVR",
        id: "cvr",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CVR",
      },
      {
        name: "AOV",
        id: "aov",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "AOV",
      },
      {
        name: "ACOS",
        id: "acos",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ACOS",
      },
      {
        name: "Campaign Budget",
        id: "campaign_budget",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Campaign Budget",
      },
      {
        name: "Burn Rate",
        id: "campaign_burn_rate",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Burn Rate",
      },
    ],
  },
};

export const FiltersCampaignDataCampaignListV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [],
  },
};

